/*
 * Profitability history of an ASIC miner on its product page (block [data-hm-hist] of snippets/hm-asic-history.liquid).
 * What THIS machine (hashrate and power read from the product title) would have earned on each day of the period:
 *   revenue(day) = machine hashrate x revenue of one unit of hashrate that day (EUR per H/s per day)
 * Data: the Prefect flow "ASIC Profitability History" of the VPS (flows/asic_history.py) publishes once a day one
 * series per algorithm, every day since mid-2019 (gpu.happymining.eu/v1/asic-history/<algo>.json, also stored in
 * ClickHouse central_data.asic_profitability_daily), rebuilt with the same method as the hourly figures; today's point
 * is the hourly figure of asic.json, so the chart ends on the figure of the calculator above. One request, loaded when
 * the block nears the screen; the block hides itself if the data cannot be loaded.
 * Electricity: the price and the number of machines of the calculator above (constant over the period);
 * payback on today's product price (no price = no payback, as in the tiles).
 */
(function () {
  'use strict';
  var HALVINGS = { 'SHA-256': ['2020-05-11', '2024-04-20'], 'Scrypt': ['2019-08-05', '2023-08-02'], 'Equihash': ['2020-11-18', '2024-11-23'] };
  var PERIODS = { '6m': 183, '1y': 365, '2y': 730, '5y': 1826 };
  var UNITS = { T: 1e12, G: 1e9, M: 1e6, K: 1e3 };
  var DAY = 864e5;
  var DAYS_PER_MONTH = 30.4;

  function iso(t) { return new Date(t).toISOString().slice(0, 10); }
  function num(v) { var n = parseFloat(String(v).replace(',', '.')); return isFinite(n) ? n : NaN; }
  function hs(hash, unit) { return (num(hash) || 0) * (UNITS[String(unit || '').trim().charAt(0).toUpperCase()] || 1); }
  function esc(s) { return String(s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function getJson(url) {
    return fetch(url, { mode: 'cors', credentials: 'omit' }).then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); });
  }

  function niceTicks(lo, hi, n) {
    var span = hi - lo || 1, step = Math.pow(10, Math.floor(Math.log(span / n) / Math.LN10)), e = span / n / step;
    step *= e >= 7.5 ? 10 : e >= 3.5 ? 5 : e >= 1.5 ? 2 : 1;
    var a = Math.floor(lo / step) * step, b = Math.ceil(hi / step) * step, out = [];
    for (var v = a; v <= b + step / 2; v += step) out.push(Math.round(v / step) * step);
    return { lo: a, hi: b, step: step, ticks: out };
  }

  function init(root) {
    var sec = root.closest('[data-hm-asic]');
    var algo = sec && sec.getAttribute('data-algo');
    if (!algo || !window.fetch) { root.hidden = true; return; }
    var i18n = {};
    try { i18n = JSON.parse(root.querySelector('script[data-hist-i18n]').textContent); } catch (e) { /* labels missing: keys shown empty */ }
    var cfg = window.hmRent || {};
    var locale = cfg.locale || document.documentElement.lang || 'fr';
    var H = hs(sec.getAttribute('data-hash'), sec.getAttribute('data-unit'));
    var P = num(sec.getAttribute('data-power')) || 0;
    var price = sec.getAttribute('data-currency') === 'EUR' ? (num(sec.getAttribute('data-price')) || 0) : 0;
    var form = sec.querySelector('form');
    var kwhIn = form && form.querySelector('[name="kwh"]'), countIn = form && form.querySelector('[name="count"]');
    var chart = root.querySelector('[data-chart]'), kpis = root.querySelector('[data-kpis]'), range = root.querySelector('[data-range]');
    var status = root.querySelector('[data-status]');
    var eur0 = new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
    var eur2 = new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
    var pct0 = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
    var dShort = new Intl.DateTimeFormat(locale, { month: 'short', year: '2-digit', timeZone: 'UTC' });
    var dYear = new Intl.DateTimeFormat(locale, { year: 'numeric', timeZone: 'UTC' });
    var dLong = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
    function t(k, vars) { var s = i18n[k] || ''; Object.keys(vars || {}).forEach(function (v) { s = s.replace('{' + v + '}', vars[v]); }); return s; }
    function money(v, f) { return '<span class="' + (v < 0 ? 'hm-rent-neg' : 'hm-rent-pos') + '">' + (f || eur0).format(v) + '</span>'; }
    function day(d) { return dLong.format(new Date(d + 'T00:00:00Z')); }

    // raw: EUR per H/s per day, by day (UTC); ready once loaded
    var raw = {}, ready = false, period = '1y', mode = 'day', cur = null, lastW = 0;
    var asicUrl = cfg.asic || 'https://gpu.happymining.eu/v1/asic.json';
    var histUrl = asicUrl.replace(/asic\.json(\?.*)?$/, 'asic-history/' + algo.toLowerCase().replace(/[^a-z0-9]/g, '') + '.json');

    function kwh() {
      var v = kwhIn ? num(kwhIn.value) : NaN;
      if (v >= 0) return v;
      var u = window.hmKwh && window.hmKwh.get ? window.hmKwh.get() : null;
      return u !== null && u >= 0 ? u : (typeof cfg.kwh === 'number' ? cfg.kwh : 0);
    }
    function count() { return Math.max(1, parseInt(countIn && countIn.value, 10) || 1); }

    function load() {
      return Promise.all([getJson(histUrl), getJson(asicUrl)]).then(function (res) {
        var h = res[0], live = res[1] && res[1].algorithms && res[1].algorithms[algo];
        var t0 = Date.parse(h.start + 'T00:00:00Z'), f = h.unit_factor || 1;
        (h.values || []).forEach(function (v, i) { if (v > 0) raw[iso(t0 + i * DAY)] = v / f; });
        if (live && live.eur_per_hs_day > 0) raw[iso(Date.now())] = live.eur_per_hs_day; // today = the calculator's figure
        if (Object.keys(raw).length < 2) throw new Error('no data');
        ready = true;
      });
    }

    function series() {
      var start = iso(Date.now() - (PERIODS[period] - 1) * DAY), n = count();
      var cost = P / 1000 * 24 * kwh() * n;
      return Object.keys(raw).sort().filter(function (d) { return d >= start; }).map(function (d) {
        var rev = raw[d] * H * n;
        return { d: d, rev: rev, cost: cost, net: rev - cost };
      });
    }

    function render() {
      if (!ready) return;
      var s = series(), N = s.length;
      if (N < 2) { root.hidden = true; return; }
      var n = count(), cost = s[0].cost, invest = price * n;
      var sum = { rev: 0, cost: 0, net: 0 }, cum = [], paid = -1, acc = 0;
      s.forEach(function (p, i) {
        sum.rev += p.rev; sum.cost += p.cost; sum.net += p.net;
        acc += p.net; cum.push(acc);
        if (paid < 0 && invest > 0 && acc >= invest) paid = i;
      });
      // daily view: trailing 7-day average (a day's blocks are a lottery)
      var avgRev = [], avgNet = [];
      s.forEach(function (p, i) {
        var a = 0, c = 0;
        for (var j = Math.max(0, i - 6); j <= i; j++) { a += s[j].rev; c++; }
        avgRev.push(a / c); avgNet.push(a / c - cost);
      });
      range.textContent = t('range', { from: day(s[0].d), to: day(s[N - 1].d) });
      var roi = '';
      if (invest > 0) {
        var months = paid >= 0 ? Math.max(1, Math.ceil((paid + 1) / DAYS_PER_MONTH)) : 0, pct = sum.net / invest * 100;
        roi = '<div><dt>' + esc(t('k_roi')) + '</dt><dd><span class="' + (paid >= 0 ? 'hm-rent-pos' : '') + '">' + pct0.format(Math.max(0, pct)) + ' %</span>' +
          '<small>' + esc(paid >= 0 ? t('roi_paid', { n: months }) : t('roi_not')) + '</small></dd></div>';
      }
      kpis.innerHTML = '<dl class="hm-rent-kpis">' +
        '<div><dt>' + esc(t('k_rev')) + '</dt><dd>' + eur0.format(sum.rev) + '<small>' + esc(t('k_rev_avg', { x: eur2.format(sum.rev / N) })) + '</small></dd></div>' +
        '<div><dt>' + esc(t('k_cost')) + '</dt><dd>' + eur0.format(sum.cost) + '<small>' + esc(t('k_cost_kwh', { x: new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }).format(kwh()) })) + '</small></dd></div>' +
        '<div><dt>' + esc(t('k_net')) + '</dt><dd>' + money(sum.net) + '<small>' + esc(t('k_net_n', { n: n })) + '</small></dd></div>' +
        roi + '</dl>';
      if (mode === 'cum') draw(s, cum, null, invest > 0 ? invest : null, paid);
      else draw(s, avgNet, cost > 0 ? avgRev : null, null, -1);
    }

    function draw(s, vals, ghost, line, mark) {
      var cw = Math.round(chart.clientWidth);
      // not laid out yet (background tab, content-visibility of the section): the ResizeObserver draws it once sized
      if (cw < 120 && window.ResizeObserver) { lastW = 0; cur = null; return; }
      var w = Math.max(280, cw), h = w < 560 ? 210 : 260;
      var L = 58, R = 12, T = 22, B = 24, iw = w - L - R, ih = h - T - B, N = vals.length;
      lastW = w;
      var lo = Math.min(0, Math.min.apply(null, vals)), hi = Math.max.apply(null, vals.concat(ghost || [], line ? [line] : []));
      if (!(hi > lo)) hi = lo + 1;
      var nt = niceTicks(lo, hi * 1.04, h < 230 ? 4 : 5);
      lo = nt.lo; hi = nt.hi;
      var fy = new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR', maximumFractionDigits: nt.step < 1 ? 2 : 0, notation: hi >= 100000 ? 'compact' : 'standard' });
      function x(i) { return L + i / (N - 1) * iw; }
      function y(v) { return T + (hi - v) / (hi - lo) * ih; }
      // inline size: the theme gives every svg a small fixed size in CSS, which beats the width / height attributes
      var o = '<svg viewBox="0 0 ' + w + ' ' + h + '" width="' + w + '" height="' + h + '" style="width:' + w + 'px;height:' + h + 'px;max-width:none" role="img" aria-label="' + esc(t('aria')) + '">';
      nt.ticks.forEach(function (v) {
        var yy = y(v).toFixed(1);
        o += '<line class="' + (v === 0 && lo < 0 ? 'z' : 'g') + '" x1="' + L + '" x2="' + (w - R) + '" y1="' + yy + '" y2="' + yy + '"/>' +
          '<text x="' + (L - 8) + '" y="' + yy + '" dy=".32em" text-anchor="end">' + esc(fy.format(v)) + '</text>';
      });
      // x axis: month boundaries, every 1/2/3/6/12/24 months depending on the room
      var first = s[0].d, firstT = Date.parse(first + 'T00:00:00Z'), spanT = Date.parse(s[N - 1].d + 'T00:00:00Z') - firstT;
      var steps = [1, 2, 3, 6, 12, 24], room = Math.max(2, Math.floor(iw / 64)), months = spanT / DAY / DAYS_PER_MONTH, step = 24;
      for (var si = 0; si < steps.length; si++) { if (months / steps[si] <= room) { step = steps[si]; break; } }
      var y0 = parseInt(first.slice(0, 4), 10), m0 = parseInt(first.slice(5, 7), 10);
      for (var mi = 1; mi < months + 2; mi++) {
        var mm = (m0 - 1 + mi) % 12, yr = y0 + Math.floor((m0 - 1 + mi) / 12);
        if (((yr * 12 + mm) % step) !== 0) continue;
        var tt = Date.UTC(yr, mm, 1), i = (tt - firstT) / DAY;
        if (i < 0 || i > N - 1) continue;
        var xx = x(i).toFixed(1);
        o += '<line class="g" x1="' + xx + '" x2="' + xx + '" y1="' + (h - B) + '" y2="' + (h - B + 4) + '"/>' +
          '<text x="' + xx + '" y="' + (h - 6) + '" text-anchor="middle">' + esc((step >= 12 ? dYear : dShort).format(new Date(tt))) + '</text>';
      }
      (HALVINGS[algo] || []).forEach(function (d) {
        var i = (Date.parse(d + 'T00:00:00Z') - firstT) / DAY;
        if (i <= 0 || i >= N - 1) return;
        var xx = x(i).toFixed(1);
        o += '<line class="hv" x1="' + xx + '" x2="' + xx + '" y1="' + T + '" y2="' + (h - B) + '"/><text class="ht" x="' + xx + '" y="' + (T - 8) + '" text-anchor="middle">' + esc(t('halving')) + '</text>';
      });
      function path(arr) { return arr.map(function (v, i) { return (i ? 'L' : 'M') + x(i).toFixed(1) + ',' + y(v).toFixed(1); }).join(''); }
      var p = path(vals), base = y(Math.max(lo, 0)).toFixed(1);
      o += '<path class="ar" d="' + p + 'L' + x(N - 1).toFixed(1) + ',' + base + 'L' + L + ',' + base + 'Z"/>';
      if (ghost) o += '<path class="rv" d="' + path(ghost) + '"/>';
      o += '<path class="ln" d="' + p + '"/>';
      if (line) {
        var yl = y(line).toFixed(1);
        o += '<line class="pl" x1="' + L + '" x2="' + (w - R) + '" y1="' + yl + '" y2="' + yl + '"/>' +
          '<text class="pt" x="' + (L + 6) + '" y="' + (+yl - 6) + '">' + esc(t('price_line') + ' · ' + eur0.format(line)) + '</text>';
      }
      if (mark >= 0) {
        var xm = x(mark), ym = y(vals[mark]), right = xm > L + iw * 0.6;
        o += '<circle class="pm" cx="' + xm.toFixed(1) + '" cy="' + ym.toFixed(1) + '" r="5"/>' +
          '<text class="pt" x="' + (right ? xm - 9 : xm + 9).toFixed(1) + '" y="' + (ym + 16).toFixed(1) + '" text-anchor="' + (right ? 'end' : 'start') + '">' + esc(t('paid_on', { date: day(s[mark].d) })) + '</text>';
      }
      o += '<g class="cur" visibility="hidden"><line y1="' + T + '" y2="' + (h - B) + '"/><circle r="4.5"/></g>';
      o += '<rect class="hit" x="' + L + '" y="0" width="' + iw + '" height="' + h + '" fill="transparent" stroke="none"/></svg>';
      o += '<div class="hm-hc__tip" hidden></div>';
      chart.innerHTML = o;
      cur = { s: s, vals: vals, ghost: ghost, x: x, y: y, L: L, iw: iw, N: N, w: w, line: line };
    }

    function hover(e) {
      if (!cur) return;
      var svg = chart.querySelector('svg'), g = chart.querySelector('.cur'), tip = chart.querySelector('.hm-hc__tip');
      if (!svg || !g || !tip) return;
      var r = svg.getBoundingClientRect(), px = (e.clientX - r.left) * (cur.w / r.width);
      var i = Math.max(0, Math.min(cur.N - 1, Math.round((px - cur.L) / cur.iw * (cur.N - 1))));
      var xx = cur.x(i), yy = cur.y(cur.vals[i]), p = cur.s[i];
      g.setAttribute('visibility', 'visible');
      g.querySelector('line').setAttribute('x1', xx); g.querySelector('line').setAttribute('x2', xx);
      g.querySelector('circle').setAttribute('cx', xx); g.querySelector('circle').setAttribute('cy', yy);
      var html = '<b>' + esc(day(p.d)) + '</b><br>';
      if (mode === 'cum') {
        html += esc(t('net')) + ' ' + eur0.format(cur.vals[i]) + (cur.line ? ' · ' + pct0.format(cur.vals[i] / cur.line * 100) + ' % ' + esc(t('of_price')) : '');
      } else {
        var rev = cur.ghost ? cur.ghost[i] : cur.vals[i];
        html += esc(t('rev')) + ' ' + eur2.format(rev) + esc(t('per_day'));
        if (cur.ghost) html += '<br>' + esc(t('net')) + ' ' + eur2.format(cur.vals[i]) + esc(t('per_day'));
        html += '<br><small>' + esc(t('avg7')) + '</small>';
      }
      tip.innerHTML = html;
      tip.hidden = false;
      var tw = tip.offsetWidth, left = xx / cur.w * r.width - tw / 2;
      tip.style.left = Math.max(0, Math.min(r.width - tw, left)) + 'px';
    }
    function leave() {
      var g = chart.querySelector('.cur'), tip = chart.querySelector('.hm-hc__tip');
      if (g) g.setAttribute('visibility', 'hidden');
      if (tip) tip.hidden = true;
    }
    chart.addEventListener('pointermove', hover);
    chart.addEventListener('pointerdown', hover);
    chart.addEventListener('pointerleave', leave);

    function setStatus(key) {
      if (!status) return;
      status.textContent = key ? t(key) : '';
      status.hidden = !key;
    }
    function press(attr, val) {
      root.querySelectorAll('[' + attr + ']').forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute(attr) === val ? 'true' : 'false'); });
    }
    root.addEventListener('click', function (e) {
      var b = e.target.closest('[data-period],[data-mode]');
      if (!b || !root.contains(b)) return;
      if (b.hasAttribute('data-mode')) { mode = b.getAttribute('data-mode'); press('data-mode', mode); render(); return; }
      var want = b.getAttribute('data-period');
      if (!PERIODS[want]) return;
      period = want; press('data-period', want); render();
    });
    if (form) form.addEventListener('input', render);
    document.addEventListener('hm:kwh', function () { setTimeout(render, 0); });
    if (window.ResizeObserver) {
      var raf = 0;
      new ResizeObserver(function () {
        if (raf) return;
        raf = requestAnimationFrame(function () { raf = 0; if (Math.abs(Math.round(chart.clientWidth) - lastW) > 4) render(); });
      }).observe(chart);
    }

    function start() {
      root.classList.add('is-loading');
      setStatus('loading');
      load().then(function () {
        root.classList.remove('is-loading');
        setStatus('');
        render();
      }).catch(function () { root.hidden = true; });
    }
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (en) {
        if (!en.some(function (x) { return x.isIntersecting; })) return;
        io.disconnect();
        start();
      }, { rootMargin: '400px 0px' });
      io.observe(root);
    } else start();
  }

  function boot() {
    document.querySelectorAll('[data-hm-hist]:not([data-ready])').forEach(function (el) { el.setAttribute('data-ready', ''); init(el); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
